import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import MediaImage from "@/components/MediaImage";
import ShareBar from "@/components/public/ShareBar";
import { fmtDate } from "@/components/public/PoemCard";
import Logo from "@/components/Logo";

export default function PoemDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [poem, setPoem] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setRelated([]);
    api.get(`/poems/${slug}`).then(({ data }) => {
      setPoem(data);
      const tag = (data.tags || [])[0];
      if (tag) {
        api.get("/poems", { params: { tag } }).then(({ data: list }) => {
          setRelated(list.filter((p) => p.slug !== data.slug).slice(0, 3));
        }).catch(() => {});
      }
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="on-dark violet-canvas flex-1 w-full">
        <div className="mx-auto max-w-3xl px-5 py-32 text-center">
          <h1 className="font-serif text-4xl">Gedicht nicht gefunden</h1>
          <Link to="/gedichte" className="mt-6 inline-block border-b border-[#F4C542] text-[#F4C542]">Zurück zu den Gedichten</Link>
        </div>
      </div>
    );
  }
  if (!poem) return <div className="on-dark violet-canvas flex-1 w-full py-32 text-center text-[#B79FD1]">Laden…</div>;

  const structured = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: poem.title,
    author: { "@type": "Person", name: "Susanne Monika Gysin" },
    datePublished: poem.date,
  };

  return (
    <div className="on-dark violet-canvas flex-1 w-full">
      <article className="fade-in mx-auto max-w-2xl px-5 sm:px-8 py-10 sm:py-20">
      <SEO title={poem.seo_title || poem.title} description={poem.seo_description || (poem.text || "").slice(0, 150)} image={mediaUrl(poem.image)} type="article" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }} />

      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#B79FD1] hover:text-[#F4C542] mb-10">
        <ChevronLeft className="w-4 h-4" /> Zurück
      </button>

      {poem.image && (
        <div className="mb-12 overflow-hidden bg-[#F3E7D2]">
          <MediaImage id={poem.image} alt={poem.title} aspect="aspect-[16/9]" className="w-full" />
        </div>
      )}

      <div className="text-center">
        {poem.category && <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-5">{poem.category}</div>}
        <h1 className="font-serif text-5xl sm:text-6xl font-light leading-tight" data-testid="poem-title">{poem.title}</h1>
        <div className="mt-4 text-sm text-[#B79FD1]">{fmtDate(poem.date)}</div>
        <Logo variant="butterfly" className="h-9 w-auto mx-auto mt-8 opacity-90" />
      </div>

      {poem.body_html ? (
        <div
          className="poem-body text-2xl leading-[1.7] text-[#FFF9F2] mt-14 text-center"
          data-testid="poem-text"
          dangerouslySetInnerHTML={{ __html: poem.body_html }}
        />
      ) : (
        <div className="poem-body text-2xl leading-[1.7] text-[#FFF9F2] mt-14 text-center" data-testid="poem-text">
          {poem.text}
        </div>
      )}

      <div className="mt-16 pt-8 border-t border-[#632A8E]/50 text-center">
        <div className="font-serif text-xl text-[#FFF9F2]">Susanne Monika Gysin</div>
      </div>

      {poem.artwork_slug && (
        <div className="mt-8 text-center">
          <Link to={`/galerie/${poem.artwork_slug}`} className="text-sm border-b border-[#F4C542] text-[#F4C542] pb-0.5">Zugehöriges Kunstwerk ansehen</Link>
        </div>
      )}

      {poem.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="poem-tags">
          {poem.tags.map((t) => (
            <Link key={t} to={`/gedichte?tag=${encodeURIComponent(t)}`} data-testid={`poem-tag-${t}`}
              className="rounded-full border border-[#632A8E] px-3.5 h-8 inline-flex items-center text-sm text-[#D8B9EA] hover:border-[#F4C542] hover:text-[#F4C542] transition-colors">
              #{t}
            </Link>
          ))}
        </div>
      )}

      {related.length > 0 && (
        <div className="mt-16 pt-10 border-t border-[#632A8E]/50" data-testid="related-poems">
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-6 text-center">Verwandte Gedichte</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/gedichte/${p.slug}`}
                data-testid={`related-poem-${p.slug}`}
                className="group block bg-[#3C1A5C] rounded-md overflow-hidden ring-1 ring-[#632A8E]/60 hover:ring-[#F4C542] transition-all"
              >
                <div className="bg-[#2A0D42]">
                  <MediaImage id={p.image} thumb alt={p.title} aspect="aspect-[4/3]" className="w-full" />
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-xl leading-tight text-[#F4C542] group-hover:text-[#D85CA4] transition-colors">{p.title}</h3>
                  {p.category && <div className="text-xs uppercase tracking-[0.12em] text-[#B79FD1] mt-1.5">{p.category}</div>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <ShareBar title={poem.title} className="mt-12 justify-center" />
      </article>
    </div>
  );
}
